import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Scale, FileText } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useContent } from "@/content";

const DOCS = {
  privacy: { title: "Privacy Policy & Legal", sub: "How Desantrix handles your information, and the rights you and we hold.", icon: Scale, key: "PRIVACY_POLICY" },
  terms: { title: "Terms of Service", sub: "The general terms that apply when you use this website or work with Desantrix.", icon: FileText, key: "TERMS_OF_SERVICE" },
};

const LegalModal = ({ doc, onClose }) => {
  const content = useContent();
  const d = doc ? { ...DOCS[doc], sections: content[DOCS[doc].key] } : null;
  return (
    <AnimatePresence>
      {d && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0B1528]/60 backdrop-blur-sm p-4"
          onClick={onClose}
          data-testid={`${doc}-modal`}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl bg-white p-8 md:p-10 shadow-2xl"
          >
            <button
              onClick={onClose}
              data-testid={`${doc}-modal-close`}
              aria-label="Close"
              className="absolute top-5 right-5 rounded-full border border-slate-200 p-2 text-slate-500 hover:text-[#0B1528] hover:border-slate-400 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/10 border border-amber-400/30">
                <d.icon className="h-5 w-5 text-amber-500" />
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-[#0B1528]">{d.title}</h3>
            </div>
            <p className="text-xs text-slate-400 mb-8">{d.sub} Last updated: June 2026.</p>
            <div className="space-y-6">
              {d.sections.map((s, i) => (
                <motion.div key={s.h} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.04 }}>
                  <h4 className="text-sm font-semibold text-[#0B1528] mb-1.5">{s.h}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.b}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Footer = ({ onNavigate }) => {
  const { NAV_LINKS, SITE, DIVISIONS, HERO } = useContent();
  const [doc, setDoc] = useState(null);

  return (
    <footer className="border-t border-slate-200 bg-[#0B1528]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <div className="grid md:grid-cols-[1.4fr_1fr_1fr] gap-12">
          <div>
            <button onClick={() => onNavigate("home")} className="mb-6" data-testid="footer-logo" aria-label="Desantrix home">
              <Logo dark />
            </button>
            <p className="max-w-sm text-sm text-slate-400 leading-relaxed">
              An international technology company from India — one partner for engineering,
              management, support and creative production, through focused divisions.
            </p>
            <div className="mt-6 space-y-2 text-sm">
              <p className="text-slate-500">New projects · <a href={`mailto:${SITE.email}`} className="text-slate-300 hover:text-amber-400 transition-colors" data-testid="footer-email">{SITE.email}</a></p>
              <p className="text-slate-500">Existing clients · <a href={`mailto:${SITE.supportEmail}`} className="text-slate-300 hover:text-amber-400 transition-colors" data-testid="footer-support-email">{SITE.supportEmail}</a></p>
            </div>
          </div>

          <div>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-slate-500 mb-5">Navigate</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => onNavigate(l.id)}
                    data-testid={`footer-nav-${l.id}`}
                    className="text-sm text-slate-400 hover:text-amber-400 hover:translate-x-1 inline-block transition-all duration-300"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-slate-500 mb-5">Our divisions</p>
            <ul className="space-y-3" data-testid="footer-divisions">
              {DIVISIONS.map((d) => (
                <li key={d.name} className="flex items-center gap-2.5 text-sm">
                  <span className={d.active ? "text-slate-200" : "text-slate-500"}>{d.name}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 font-mono text-[9px] tracking-[0.15em] uppercase border ${
                      d.active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-white/5 text-slate-500 border-white/10"
                    }`}
                  >
                    {d.status}
                  </span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => onNavigate("contact")}
              data-testid="footer-quote-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full border border-amber-400/40 px-5 py-2.5 text-xs font-semibold text-amber-400 hover:bg-amber-400 hover:text-[#0B1528] transition-colors duration-300"
            >
              {HERO.primaryCta}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500" data-testid="footer-copyright">© 2026 Desantrix. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <button onClick={() => setDoc("privacy")} data-testid="footer-privacy-link" className="text-xs text-slate-500 hover:text-amber-400 transition-colors">
              Privacy Policy & Legal
            </button>
            <button onClick={() => setDoc("terms")} data-testid="footer-terms-link" className="text-xs text-slate-500 hover:text-amber-400 transition-colors">
              Terms of Service
            </button>
          </div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-600" data-testid="footer-credit">Made by Desantrix Web</p>
        </div>
      </div>
      <LegalModal doc={doc} onClose={() => setDoc(null)} />
    </footer>
  );
};
